/**
 * User Settings Context
 * Global state management for user settings including MFA, Comply Admin, and auto-suspend
 * Provides centralized access to user preferences across the application
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { isAdminApiEnabled } from "../services/AdminApiService";
import { BASE_URL } from "../App_Settings/app_settings";

// Create context
const UserSettingsContext = createContext();

// Custom hook to use the user settings context
export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
};

/**
 * User Settings Provider Component
 * Manages global user settings state and provides methods to access/update settings
 */
export const UserSettingsProvider = ({ children }) => {
  const [hasComplyAdmin, setHasComplyAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load organization settings from API using centralized service
   * For super admins, always set hasComplyAdmin to true
   */
  useEffect(() => {
    const loadOrganizationSettings = async () => {
      try {
        setIsLoading(true);
        console.log("UserSettingsContext: Loading organization settings...");

        // Get user data from localStorage
        const userDataJSON = localStorage.getItem("token");
        if (!userDataJSON) {
          setHasComplyAdmin(false);
          setIsLoading(false);
          return;
        }

        const userData = JSON.parse(userDataJSON);
        
        // Fetch user details from API to check if super admin
        const response = await fetch(`${BASE_URL}/users/${userData.userId}/${userData.organizationId}`, {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        });

        if (response.ok) {
          const user = await response.json();
          const isSuperAdmin = user?.isSuperAdmin || false;

          console.log("UserSettingsContext: isSuperAdmin status:", isSuperAdmin);

          if (isSuperAdmin) {
            // Super admin always has Comply Admin access
            console.log("UserSettingsContext: Super Admin detected - enabling Comply Admin");
            setHasComplyAdmin(true);
          } else {
            // Use centralized AdminApiService for non-super admins
            const isEnabled = await isAdminApiEnabled();
            console.log("UserSettingsContext: Send To Admin API enabled:", isEnabled);
            setHasComplyAdmin(isEnabled);
          }
        } else {
          console.error("UserSettingsContext: Failed to fetch user details");
          // Fall back to checking admin API
          const isEnabled = await isAdminApiEnabled();
          setHasComplyAdmin(isEnabled);
        }
      } catch (error) {
        console.error("UserSettingsContext: Error loading organization settings:", error);
        setHasComplyAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganizationSettings();
  }, []);

  /**
   * Method to refresh settings (call after updating in AdminSettings)
   * Uses centralized AdminApiService
   * For super admins, always returns true
   */
  const refreshSettings = async () => {
    try {
      console.log("UserSettingsContext: Refreshing settings...");

      // Get user data from localStorage
      const userDataJSON = localStorage.getItem("token");
      if (!userDataJSON) {
        setHasComplyAdmin(false);
        return;
      }

      const userData = JSON.parse(userDataJSON);
      
      // Fetch user details from API to check if super admin
      const response = await fetch(`${BASE_URL}/users/${userData.userId}/${userData.organizationId}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      });

      if (response.ok) {
        const user = await response.json();
        const isSuperAdmin = user?.isSuperAdmin || false;

        console.log("UserSettingsContext: Refresh - isSuperAdmin status:", isSuperAdmin);

        if (isSuperAdmin) {
          // Super admin always has Comply Admin access
          console.log("UserSettingsContext: Super Admin detected - Comply Admin always enabled");
          setHasComplyAdmin(true);
        } else {
          // Use centralized AdminApiService for non-super admins
          const isEnabled = await isAdminApiEnabled();
          console.log("UserSettingsContext: Refreshed - Send To Admin API enabled:", isEnabled);
          setHasComplyAdmin(isEnabled);
        }
      } else {
        // Fall back to checking admin API
        const isEnabled = await isAdminApiEnabled();
        setHasComplyAdmin(isEnabled);
      }
    } catch (error) {
      console.error("UserSettingsContext: Error refreshing settings:", error);
    }
  };

  const value = {
    hasComplyAdmin,
    isLoading,
    refreshSettings,
    setHasComplyAdmin, // Expose setter for manual updates
  };

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
};

export default UserSettingsContext;
